import { useEffect } from "react";

/**
 * Custom hook to handle fullscreen functionality for tablet interfaces
 */
export const useFullscreen = (autoEnter = true) => {
  useEffect(() => {
    // Only add event listeners and auto-behavior if autoEnter is true
    if (!autoEnter) {
      return; // Skip all automatic behavior when manually controlling
    }

    // Auto-enter fullscreen when component mounts (if enabled)
    if (autoEnter) {
      enterFullscreen();
    }

    // Add event listeners for visibility changes
    const handleVisibilityChange = () => {
      // Re-enter fullscreen if user exits and returns to app
      if (!document.hidden && autoEnter) {
        setTimeout(() => {
          if (!isFullscreen()) {
            enterFullscreen();
          }
        }, 500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [autoEnter]);

  const isFullscreen = () => {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
  };

  const enterFullscreen = async () => {
    try {
      const element = document.documentElement;

      // Try different fullscreen methods for cross-browser compatibility
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      }

      console.log("✅ Fullscreen mode activated");
    } catch (error) {
      // Fullscreen might be blocked by browser or user, this is normal
      console.log("ℹ️ Fullscreen mode not available:", error.message);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }

      console.log("✅ Fullscreen mode deactivated");
    } catch (error) {
      console.log("❌ Error exiting fullscreen:", error.message);
    }
  };

  const toggleFullscreen = () => {
    if (isFullscreen()) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  return {
    isFullscreen: isFullscreen(),
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
  };
};

export default useFullscreen;
